// Sugalarm — haptic feedback helper (cycle 115).
//
// The ONLY place haptics are invoked from. Three semantic functions with
// internal feature-detection and graceful degradation:
//   - Capacitor native platform → @capacitor/haptics (dynamic import, the
//     same /* @vite-ignore */ + Rollup-external pattern lib/capacitor.ts
//     and lib/nativePush.ts use, so the web bundle never resolves the
//     plugin and browsers without it keep working).
//   - Non-native with navigator.vibrate (Android browsers) → a short
//     vibration fallback.
//   - Otherwise → silent no-op.
//
// Every call is fire-and-forget and never throws. Callers must already be
// on the mobile variant — there are no haptics on kiosk / web paths.

import { isCapacitorNative } from "./capacitor";

// ImpactStyle / NotificationType string literals mirror the plugin's enums
// so we don't need a static import (which would pull the plugin into the
// web bundle). The plugin accepts these string values.
type Impact = "LIGHT" | "MEDIUM" | "HEAVY";

interface HapticsProxy {
  impact: (opts: { style: Impact }) => Promise<void>;
  selectionStart: () => Promise<void>;
  selectionChanged: () => Promise<void>;
  selectionEnd: () => Promise<void>;
  notification: (opts: { type: string }) => Promise<void>;
}

// The pure-remote webview can't resolve a bare `import("@capacitor/haptics")`
// at runtime — natively-installed plugins are reached via the bridge-injected
// `window.Capacitor.Plugins` global instead.
function hapticsProxy(): HapticsProxy | null {
  const cap = (
    window as { Capacitor?: { Plugins?: { Haptics?: HapticsProxy } } }
  ).Capacitor;
  return cap?.Plugins?.Haptics ?? null;
}

async function impact(style: Impact): Promise<void> {
  try {
    if (isCapacitorNative()) {
      await hapticsProxy()?.impact({ style });
      return;
    }
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(style === "MEDIUM" ? 18 : 10);
    }
  } catch {
    // Fire-and-forget — a missing plugin / denied permission is a no-op.
  }
}

async function selection(): Promise<void> {
  try {
    if (isCapacitorNative()) {
      const h = hapticsProxy();
      if (h) {
        await h.selectionStart();
        await h.selectionChanged();
        await h.selectionEnd();
      }
      return;
    }
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(10);
    }
  } catch {
    /* no-op */
  }
}

async function notify(): Promise<void> {
  try {
    if (isCapacitorNative()) {
      await hapticsProxy()?.notification({ type: "SUCCESS" });
      return;
    }
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate([12, 40, 12]);
    }
  } catch {
    /* no-op */
  }
}

/** A light tap — keypad digits + backspace. */
export function hapticTap(): void {
  void impact("LIGHT");
}

/** A selection change — type pills, "when" chips, window pills, rail
 *  tabs, IOB pager page commit. */
export function hapticSelect(): void {
  void selection();
}

/** A success notification — a completed log submit. */
export function hapticSuccess(): void {
  void notify();
}
